<template>
  <transition name="release-drawer">
    <aside
      v-if="visible"
      class="release-drawer"
      role="complementary"
      :aria-label="$t('whatsNew.title')"
    >
      <header class="release-drawer__header">
        <div class="release-drawer__title-row">
          <span class="material-icons release-drawer__celebration" aria-hidden="true">
            auto_awesome
          </span>
          <h2>{{ $t('whatsNew.title') }}</h2>
          <span class="release-drawer__new-badge">{{ $t('whatsNew.new') }}</span>
        </div>
        <button
          type="button"
          class="release-drawer__close"
          :aria-label="$t('whatsNew.announcement.close')"
          @click="dismiss"
        >
          <span class="material-icons" aria-hidden="true">close</span>
        </button>
      </header>

      <div class="release-drawer__intro">
        <strong>{{ $t('whatsNew.announcement.heading') }}</strong>
        <p>{{ $t('whatsNew.announcement.message') }}</p>
      </div>

      <div class="release-drawer__updates">
        <div v-for="update in updates" :key="update.title" class="release-drawer__update">
          <span class="release-drawer__icon">
            <i class="material-icons" aria-hidden="true">{{ update.icon }}</i>
          </span>
          <div>
            <h3>{{ update.title }}</h3>
            <p>{{ update.summary }}</p>
          </div>
        </div>
      </div>

      <footer class="release-drawer__footer">
        <button type="button" class="release-drawer__action" @click="viewAll">
          {{ $t('whatsNew.announcement.viewAll') }}
          <span
            class="material-icons release-drawer__directional-icon"
            aria-hidden="true"
          >arrow_forward</span>
        </button>
      </footer>
    </aside>
  </transition>
</template>

<script>
import { marked } from 'marked';
import {
  latestRelease,
  loadReleaseBody,
  dismissLatestAnnouncement,
  markLatestSeen,
  shouldShowLatestAnnouncement,
} from '@/whats-new/releases';

const PREVIEW_COUNT = 4;

export default {
  name: 'WhatsNewReleaseDrawer',
  data() {
    return {
      visible: false,
      updates: [],
    };
  },
  watch: {
    // Only worth re-fetching while the drawer is actually on screen; hidden, there is nothing to
    // re-render in the new language.
    '$i18n.locale': function reloadOnLocale() {
      if (this.visible) this.loadPreview();
    },
  },
  async mounted() {
    this.$eventHub.$on('whats-new-seen', this.hide);

    // Decided before the content is fetched: this component mounts with the layout, so on every
    // page of every session. Loading the release markdown only to discard it is the common path
    // once a user has read or dismissed the announcement.
    const userData = this.$store.state.auth.userData || {};
    const isWhatsNewRoute = ['whats-new', 'whats-new-release'].includes(this.$route.name);
    if (isWhatsNewRoute || !shouldShowLatestAnnouncement(userData.userId)) return;

    await this.loadPreview();
    this.visible = true;
  },
  beforeDestroy() {
    this.$eventHub.$off('whats-new-seen', this.hide);
  },
  methods: {
    hide() {
      this.visible = false;
    },
    dismiss() {
      const userData = this.$store.state.auth.userData || {};
      dismissLatestAnnouncement(userData.userId);
      this.hide();
    },
    viewAll() {
      const userData = this.$store.state.auth.userData || {};
      markLatestSeen(userData.userId);
      this.$eventHub.$emit('whats-new-seen');
      if (this.$route.name !== 'whats-new') {
        this.$router.push({ name: 'whats-new' });
      }
    },
    async loadPreview() {
      const release = latestRelease();
      if (!release) return;

      try {
        const body = String(await loadReleaseBody(release.id, this.$i18n.locale));
        const documentBody = new DOMParser().parseFromString(marked.parse(body), 'text/html');
        // The closing section is guidance, not an update — WhatsNew.vue renders it apart as
        // "Good to know". Dropping it keeps a short release from advertising it as a feature.
        const allHeadings = Array.from(documentBody.querySelectorAll('h2'));
        const featureHeadings = allHeadings.length > 1 ? allHeadings.slice(0, -1) : allHeadings;
        const headings = featureHeadings.slice(0, PREVIEW_COUNT);
        this.updates = headings.map((heading, index) => {
          let content = heading.nextElementSibling;
          while (content && !['P', 'UL', 'OL'].includes(content.tagName)) {
            content = content.nextElementSibling;
          }
          const firstItem = content && content.querySelector ? content.querySelector('li') : null;
          const source = firstItem || content;
          const text = source ? source.textContent.replace(/\s+/g, ' ').trim() : '';
          return {
            title: heading.textContent.trim(),
            icon: (release.icons || [])[index] || 'new_releases',
            summary: text.length > 115 ? `${text.slice(0, 112).trim()}…` : text,
          };
        });
      } catch (error) {
        this.updates = [];
      }
    },
  },
};
</script>

<style scoped>
.release-drawer {
  position: fixed;
  /* Below the modal backdrop (1040) and modal (1050): an announcement must never cover a dialog
     the user is working in. The popups that deliberately sit above modals use 1080 and higher. */
  z-index: 1030;
  inset-block: 4.75rem 1rem;
  inset-inline-end: 1rem;
  display: flex;
  flex-direction: column;
  width: min(400px, calc(100vw - 2rem));
  max-height: min(680px, calc(100vh - 5.75rem));
  overflow: hidden;
  background: #fff;
  border: 1px solid rgba(216, 228, 242, 0.95);
  border-radius: 0.75rem;
  box-shadow: 0 0.75rem 2rem rgba(24, 59, 99, 0.14);
  color: #31506f;
}
.release-drawer__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  gap: 1rem;
  padding: 1.15rem 1.25rem 0.85rem;
}
.release-drawer__title-row {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}
.release-drawer__title-row h2 {
  margin: 0;
  color: #1d3a55;
  font-size: 1.2rem;
  font-weight: 700;
}
.release-drawer__celebration {
  flex: 0 0 auto;
  color: var(--fitnet-primary);
  font-size: 1.05rem;
}
.release-drawer__new-badge {
  padding: 0.15rem 0.5rem;
  background: #1c7a4d;
  border-radius: 999px;
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 1.2;
}
.release-drawer__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 2rem;
  width: 2rem;
  height: 2rem;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 0.4rem;
  color: #7a8ea8;
  cursor: pointer;
}
.release-drawer__close:hover,
.release-drawer__close:focus-visible {
  background: rgba(0, 152, 184, 0.07);
  color: var(--fitnet-primary);
  outline: none;
}
.release-drawer__close .material-icons {
  font-size: 1.2rem;
}
.release-drawer__intro {
  flex: 0 0 auto;
  padding: 0 1.25rem 1rem;
  border-bottom: 1px solid rgba(216, 228, 242, 0.8);
}
.release-drawer__intro strong {
  display: block;
  margin-bottom: 0.2rem;
  color: #31506f;
  font-size: 0.9rem;
}
.release-drawer__intro p,
.release-drawer__update p {
  margin: 0;
  color: #6d879e;
  font-size: 0.82rem;
  line-height: 1.55;
}
.release-drawer__update p {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.release-drawer__updates {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0 1.25rem;
}
.release-drawer__update {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.9rem 0;
  border-bottom: 1px solid rgba(216, 228, 242, 0.68);
}
.release-drawer__update:last-child {
  border-bottom: 0;
}
.release-drawer__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 2.1rem;
  width: 2.1rem;
  height: 2.1rem;
  background: rgba(0, 152, 184, 0.09);
  border-radius: 0.5rem;
  color: var(--fitnet-primary);
}
.release-drawer__icon .material-icons {
  font-size: 1.1rem;
}
.release-drawer__update h3 {
  margin: 0 0 0.18rem;
  color: #1d3a55;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.4;
}
.release-drawer__footer {
  flex: 0 0 auto;
  padding: 1rem 1.25rem 1.15rem;
  border-top: 1px solid rgba(216, 228, 242, 0.8);
}
.release-drawer__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  min-height: 2.55rem;
  padding: 0.55rem 1rem;
  background: var(--fitnet-primary);
  border: 1px solid var(--fitnet-primary);
  border-radius: 0.5rem;
  color: #fff;
  font-size: 0.86rem;
  font-weight: 600;
  cursor: pointer;
}
.release-drawer__action:hover,
.release-drawer__action:focus-visible {
  background: var(--fitnet-primary-button-bg-hover);
  border-color: var(--fitnet-primary-button-bg-hover);
  color: #fff;
  outline: none;
}
.release-drawer__action .material-icons {
  font-size: 1rem;
}
.release-drawer__directional-icon:dir(rtl) {
  transform: rotate(180deg);
}
.release-drawer-enter-active,
.release-drawer-leave-active {
  transition: opacity 200ms ease, transform 220ms ease;
}
.release-drawer-enter,
.release-drawer-leave-to {
  opacity: 0;
  transform: translateX(110%);
}
[dir='rtl'] .release-drawer-enter,
[dir='rtl'] .release-drawer-leave-to {
  transform: translateX(-110%);
}
@media (max-width: 575.98px) {
  .release-drawer {
    inset-block: 4.25rem 0.75rem;
    inset-inline: 0.75rem;
    width: auto;
    max-height: calc(100vh - 5rem);
  }
}
@media (prefers-reduced-motion: reduce) {
  .release-drawer-enter-active,
  .release-drawer-leave-active {
    transition: none;
  }
}
</style>
